import type { MultiDefnProps } from 'configuration-schemas/confdefnComponentProps';
import { isNil } from 'lodash';
import { createSelector, type Selector } from 'reselect';
import { HashType, getUniqueDataFromCache, isDataLoaded } from 'src/services/pivotServiceCache';
import type { AppState } from 'src/reducers';
import { ViewDataState } from 'src/types/Domain';
import type { Pivot, TreePivot } from 'src/worker/pivotWorker.types';

//#region FlowSheetByStyle component selectors

const selectCompanionViewDataState: Selector<AppState, ViewDataState> = (state) =>
  state.pages.assortmentBuild.flowSheet.viewDataStateCompanion;
const selectMassEditViewDataState: Selector<AppState, ViewDataState> = (state) =>
  state.pages.assortmentBuild.flowSheet.viewDataStateMassEdit;
const selectOverTimeViewDataState: Selector<AppState, ViewDataState> = (state) =>
  state.pages.assortmentBuild.flowSheet.viewDataStateOverTime;
const selectWorklistLiveData: Selector<AppState, Pivot> = (state) => state.worklist.liveData;
const selectMassEditData: Selector<AppState, TreePivot> = (state) =>
  state.pages.assortmentBuild.flowSheet.massEditData?.tree ?? [];
export const selectFlowSheetCompanionData: Selector<AppState, TreePivot> = (state) => {
  return getUniqueDataFromCache(state.pages.assortmentBuild.flowSheet, HashType.flowsheetCompanion)?.tree ?? [];
};
export const selectFlowSheetOverTimeData: Selector<AppState, TreePivot> = (state) => {
  return getUniqueDataFromCache(state.pages.assortmentBuild.flowSheet, HashType.flowsheetOverTime)?.tree ?? [];
};

/** Determines if a viewdefn value was configured */
export const selectFlowSheetHasMassEditViewDefn: Selector<AppState, boolean, [MultiDefnProps]> = (_state, defns) =>
  defns.view.length >= 6;
export const selectFlowSheetHasMassEditModel: Selector<AppState, boolean, [string | undefined]> = (
  _state,
  massEditModel,
) => !isNil(massEditModel);

interface FlowSheetMassEditInputs {
  defns: MultiDefnProps;
  massEditModel: string | undefined;
}

interface FlowSheetMassEditOutputs {
  hasMassEditModel: boolean;
  hasMassEditViewDefn: boolean;
}

/** A wrapper function to get both non redux inputs (view + model) into a selector */
export const selectFlowSheetMassEditInputs: Selector<AppState, FlowSheetMassEditOutputs, [FlowSheetMassEditInputs]> = (
  _state,
  inputs,
) => {
  const { defns, massEditModel } = inputs;
  return {
    hasMassEditModel: selectFlowSheetHasMassEditModel(_state, massEditModel),
    hasMassEditViewDefn: selectFlowSheetHasMassEditViewDefn(_state, defns),
  };
};

/**
 * Retrieve the viewDataStates to be included in the view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectFlowSheetViewDataStates = createSelector(
  selectCompanionViewDataState,
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectFlowSheetHasMassEditViewDefn,
  (viewDataStateCompanion, viewDataStateMassEdit, viewDataStateOverTime, hasMassEditViewDefn) => {
    const viewDataStates = hasMassEditViewDefn
      ? [viewDataStateCompanion, viewDataStateOverTime, viewDataStateMassEdit]
      : [viewDataStateCompanion, viewDataStateOverTime];
    return viewDataStates;
  },
);

/**
 * Retrieve just the massEdit loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectFlowSheetMassEditDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectFlowSheetMassEditInputs,
  (viewDataStateMassEdit, massEditInputs) => {
    const { hasMassEditViewDefn, hasMassEditModel } = massEditInputs;
    let dataLoaded;

    if (hasMassEditModel) {
      dataLoaded = isDataLoaded(viewDataStateMassEdit);
    } else if (hasMassEditViewDefn) {
      // when a viewdefn is configured but no model, data will be ready by the time this component is loaded
      dataLoaded = true;
    } else {
      dataLoaded = false;
    }
    return dataLoaded;
  },
);

//#endregion

//#region TabbedFlowSheetGrid component selectors

export const selectTabbedFlowSheetHasMassEditViewDefn: Selector<AppState, boolean, [string[]]> = (_state, viewDefns) =>
  viewDefns.length >= 2;

/**
 * Retrieve massEdit data.
 *
 * For legacy reasons when no massEdit data fetch occurs, the worklist data is returned by default.
 * */
export const selectTabbedFlowSheetMassEditData = createSelector(
  selectWorklistLiveData,
  selectMassEditData,
  selectFlowSheetHasMassEditModel,
  (worklistData, massEditData, hasMassEditModel) => {
    return hasMassEditModel ? massEditData : worklistData.tree;
  },
);

/** A wrapper function to get both non redux inputs (view + model) into a selector */
export const selectTabbedFlowSheetMassEditInputs: Selector<
  AppState,
  FlowSheetMassEditOutputs,
  [string | undefined, string[]]
> = (_state, massEditModel, viewDefns) => {
  return {
    hasMassEditModel: selectFlowSheetHasMassEditModel(_state, massEditModel),
    hasMassEditViewDefn: selectTabbedFlowSheetHasMassEditViewDefn(_state, viewDefns),
  };
};

/**
 * Retrieve tabbed view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedFlowSheetDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectFlowSheetHasMassEditModel,
  (viewDataStateMassEdit, viewDataStateOverTime, hasMassEditModel) => {
    const massEditLoaded = isDataLoaded(viewDataStateMassEdit);
    const overTimeLoaded = isDataLoaded(viewDataStateOverTime);
    return hasMassEditModel ? massEditLoaded && overTimeLoaded : overTimeLoaded;
  },
);

/**
 * Retrieve just the massEdit loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedFlowSheetMassEditDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectTabbedFlowSheetMassEditInputs,
  (viewDataStateMassEdit, massEditInputs) => {
    const { hasMassEditViewDefn, hasMassEditModel } = massEditInputs;
    let dataLoaded;

    if (hasMassEditModel) {
      dataLoaded = isDataLoaded(viewDataStateMassEdit);
    } else if (hasMassEditViewDefn) {
      // when a viewdefn is configured but no model, data will be ready by the time this component is loaded
      dataLoaded = true;
    } else {
      dataLoaded = false;
    }
    return dataLoaded;
  },
);

/**
 * Retrieve the viewDataStates to be included in the tabbed view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedFlowSheetViewDataStates = createSelector(
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectFlowSheetHasMassEditModel,
  (viewDataStateMassEdit, viewDataStateOverTime, hasMassEditModel) => {
    const viewDataStates = hasMassEditModel ? [viewDataStateOverTime, viewDataStateMassEdit] : [viewDataStateOverTime];
    return viewDataStates;
  },
);

//#endregion
